import { put, takeLatest, call } from "redux-saga/effects";
import { message } from "antd";
import api from "service/apiService";
import { t } from "@lingui/macro";
import { translate } from "utils";
import { PayloadAction } from "@reduxjs/toolkit";
import { MESSAGE_OVERLAPPED } from "constants/constants";
import { BANNER_POSITION, BANNER_TYPE } from "constants/banner";
import {
	GetListBannerPayload,
	webappHorizontalBannerSlice,
} from "./wepapp-banner.slice";
import {
	CreateMultipleBannerPayload,
	GetDetailBannerPayload,
	GetDirectionBannersPayload,
	RemoveBannersByGroupCodePayload,
	UpdateBannersOrderPayload,
	UpdateMutipleBannerPayload,
	UpdateStatusBannerByGroupCodePayload,
} from "features/banner/redux/banner.slice";

const {
	setListBanner,
	setWebappBanners,
	setWebappBannerDetail,
	setDetailBanner,
	removeBannersByGroupCode,
} = webappHorizontalBannerSlice.actions;
const {
	updateBannersOrderAsync,
	getListBannerAsync,
	getDirectionBannersAsync,
	getDetailBannerAsync,
	createMultipleBannerAsync,
	updateMultipleBannerAsync,
	updateStatusBannerByGroupCodeAsync,
	removeBannersByGroupCodeAsync,
} = webappHorizontalBannerSlice.selectors;

// Get list
function* getListBannerSaga(action: PayloadAction<GetListBannerPayload>) {
	const { query, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const response = await api.get("/dashboard/banners", {
				...query,
				skip: query.skip,
			});

			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		callback && callback();
		yield put({ type: setListBanner.type, payload: response.data });
	} else {
		message.error(translate(error.message));
	}
}

function* getDirectionBannersSaga(
	action: PayloadAction<GetDirectionBannersPayload>,
) {
	const { query, callback, type } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const response = await api.get("/dashboard/banners", query);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		callback && callback();
		yield put({ type: setWebappBanners.type, payload: response.data });
	} else {
		message.error(translate(error.message));
	}
}

function* watchGetDirectionBanners() {
	yield takeLatest(getDirectionBannersAsync, getDirectionBannersSaga);
}

function* getDetailBannerSaga(action: PayloadAction<GetDetailBannerPayload>) {
	const { id, type } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const response = await api.get(`/dashboard/banners/group/${id}`);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		if (type === BANNER_TYPE.WEB_HORIZONTAL) {
			yield put({
				type: setWebappBannerDetail.type,
				payload: response.data,
			});
		} else {
			yield put({ type: setDetailBanner.type, payload: response.data });
		}
	} else {
		message.error(translate(error.message));
	}
}

function* watchGetListBanner() {
	yield takeLatest(getListBannerAsync, getListBannerSaga);
}

function* watchGetDetailBanner() {
	yield takeLatest(getDetailBannerAsync, getDetailBannerSaga);
}

// Create multiple
function* createMultipleBannerSaga(
	action: PayloadAction<CreateMultipleBannerPayload>,
) {
	const { data, callback } = action.payload;
	let payload = JSON.parse(JSON.stringify(data));
	payload = payload.map((d: any) => ({ ...d, order: undefined }));
	const { response, error } = yield call(async () => {
		try {
			const response = await api.post(
				"/dashboard/banners/create-multiple",
				payload,
			);

			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		callback && callback();
		message.success(t`Create successful`);
	} else {
		message.error(translate(error.message));
	}
}

function* createMultipleBannerAdsSaga(
	action: PayloadAction<CreateMultipleBannerPayload>,
) {
	const { data, callback, failureCallback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const response = await api.post(
				"/dashboard/banner-ads/create-multiple",
				data,
			);

			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		callback && callback();
		message.success(t`Create successful`);
	} else {
		failureCallback && failureCallback();
		if (error.code === MESSAGE_OVERLAPPED.CODE) {
			message.error(t`The priority is overlapped with another banner`);
		}
	}
}

function* watchCreateMultipleBanner() {
	yield takeLatest(createMultipleBannerAsync, createMultipleBannerSaga);
}

// Update multiple
function* updateMultipleBannerSaga(
	action: PayloadAction<UpdateMutipleBannerPayload>,
) {
	const { data, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const response = await api.patch(
				"/dashboard/banners/update-multiple",
				data,
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		callback && callback();
		message.success(t`Update successful`);
	} else {
		if (error.code === MESSAGE_OVERLAPPED.CODE) {
			message.error(t`The priority is overlapped with another banner`);
		}
	}
}
function* watchUpdateMultipleBanner() {
	yield takeLatest(updateMultipleBannerAsync, updateMultipleBannerSaga);
}

// Update status by group code
function* updateStatusBannerByGroupCodeSaga(
	action: PayloadAction<UpdateStatusBannerByGroupCodePayload>,
) {
	const { data, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const response = await api.post(
				"/dashboard/banners/status-by-group-code",
				data,
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		callback && callback();
		message.success(t`Update successful`);
	} else {
		message.error(translate(error.message));
	}
}
function* watchUpdateStatusBannerByGroupCode() {
	yield takeLatest(
		updateStatusBannerByGroupCodeAsync,
		updateStatusBannerByGroupCodeSaga,
	);
}

function* removeBannersByGroupCodeSaga(
	action: PayloadAction<RemoveBannersByGroupCodePayload>,
) {
	const { groupCode, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const response = await api.post(
				"/dashboard/banners/status-by-group-code",
				{
					groupCode,
					isRemoved: true,
				},
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		callback && callback();
		message.success(t`Remove successful`);
		yield put({ type: removeBannersByGroupCode.type, payload: response.data });
	} else {
		message.error(translate(error.message));
	}
}
function* watchRemoveBannersByGroupCode() {
	yield takeLatest(removeBannersByGroupCodeAsync, removeBannersByGroupCodeSaga);
}

function* updateBannersOrderSaga(
	action: PayloadAction<UpdateBannersOrderPayload>,
) {
	const { data, callback, isShowMessage = true } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const response = await api.post("/dashboard/banners/update-order", data);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		callback && callback();
		isShowMessage && message.success(t`Update successful`);
	} else {
		message.error(translate(error.message));
	}
}

function* watchupdateBannersOrder() {
	yield takeLatest(updateBannersOrderAsync, updateBannersOrderSaga);
}

const webappHorizontalBannerSaga = [
	watchGetListBanner(),
	watchGetDetailBanner(),
	watchCreateMultipleBanner(),
	watchUpdateStatusBannerByGroupCode(),
	watchUpdateMultipleBanner(),
	watchGetDirectionBanners(),
	watchRemoveBannersByGroupCode(),
	watchupdateBannersOrder(),
];
export default webappHorizontalBannerSaga;
